import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, Info, Plus, Zap, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../contexts/WalletContext';
import WalletRequired from '../components/WalletRequired';
import VerificationMilestone from '../components/VerificationMilestone';
import { supabase } from '../lib/supabase';
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { PLATFORM_WALLET_ADDRESS } from '../utils/marketplaceTransactions';

const VerificationPage = () => {
  const { publicKey, isConnected, wallet } = useWallet();
  const navigate = useNavigate();
  
  // Sales count and verification milestone state
  const [creatorStats, setCreatorStats] = useState({
    salesCount: 0,
    isVerified: false,
    verificationType: 'none'
  });
  const [loading, setLoading] = useState(true);
  
  // Payment state
  const [isPaying, setIsPaying] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Fetch creator stats for verification milestone
  useEffect(() => {
    const fetchCreatorStats = async () => {
      if (!publicKey) {
        setLoading(false);
        return;
      }
      
      try {
        const walletAddress = publicKey.toString();
        const { data, error } = await supabase
          .from('creators')
          .select('sales_count, is_verified, verification_type')
          .eq('wallet', walletAddress)
          .maybeSingle();
        
        if (error) {
          console.error('Error fetching creator stats:', error);
          setLoading(false);
          return;
        }
        
        if (data) {
          setCreatorStats({
            salesCount: data.sales_count || 0,
            isVerified: data.is_verified || false,
            verificationType: data.verification_type || 'none'
          });
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching creator stats:', err);
        setLoading(false);
      }
    };
    
    fetchCreatorStats();
  }, [publicKey]);

  // Handle paid verification
  const handlePaidVerification = async () => {
    if (!wallet?.connected || !publicKey) {
      setPaymentError('Please connect your wallet first');
      return;
    }

    setIsPaying(true);
    setPaymentError(null);
    setPaymentSuccess(false);

    try {
      console.log('💰 Starting paid verification...');
      console.log('Wallet:', publicKey.toString());
      console.log('Platform wallet:', PLATFORM_WALLET_ADDRESS);

      // Get RPC connection - use Helius for better reliability
      const heliusKey = import.meta.env.VITE_HELIUS_API_KEY;
      const cluster = import.meta.env.VITE_SOLANA_CLUSTER || 'mainnet-beta';
      const clusterUrl = heliusKey && cluster === 'mainnet-beta'
        ? `https://mainnet.helius-rpc.com/?api-key=${heliusKey}`
        : cluster === 'mainnet-beta' 
          ? 'https://api.mainnet-beta.solana.com'
          : 'https://api.devnet.solana.com';
      
      const connection = new Connection(clusterUrl, 'confirmed');

      // Check balance
      const balance = await connection.getBalance(publicKey);
      const balanceSOL = balance / LAMPORTS_PER_SOL;
      console.log('💳 Balance:', balanceSOL, 'SOL');

      if (balanceSOL < 0.1) {
        throw new Error(`Insufficient balance. You need 0.1 SOL but only have ${balanceSOL.toFixed(4)} SOL`);
      }

      // Create transaction to send 0.1 SOL to platform wallet
      const transaction = new Transaction();
      const paymentAmount = Math.floor(0.1 * LAMPORTS_PER_SOL); // 0.1 SOL in lamports

      transaction.add(
        SystemProgram.transfer({
          fromPubkey: publicKey,
          toPubkey: new PublicKey(PLATFORM_WALLET_ADDRESS),
          lamports: paymentAmount
        })
      );

      // Get recent blockhash
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      console.log('📤 Sending payment transaction...');
      
      // Send transaction
      const signature = await wallet.sendTransaction(transaction, connection, {
        skipPreflight: false,
        preflightCommitment: 'confirmed'
      });

      console.log('⏳ Confirming transaction:', signature);
      await connection.confirmTransaction(signature, 'confirmed');
      console.log('✅ Payment confirmed:', signature);

      // Update creator in database
      console.log('💾 Updating database...');
      const walletAddress = publicKey.toString();
      
      // Check if creator exists
      const { data: existingCreator, error: fetchError } = await supabase
        .from('creators')
        .select('id, wallet, is_verified')
        .eq('wallet', walletAddress)
        .maybeSingle();

      if (fetchError) {
        console.error('Error fetching creator:', fetchError);
        throw new Error('Failed to verify creator status');
      }

      if (existingCreator?.is_verified) {
        throw new Error('You are already verified');
      }

      // Update or create creator
      if (existingCreator) {
        const { error: updateError } = await supabase
          .from('creators')
          .update({
            is_verified: true,
            verification_type: 'paid',
            verification_date: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('wallet', walletAddress);

        if (updateError) {
          console.error('Error updating creator:', updateError);
          throw new Error('Failed to update verification status');
        }
      } else {
        // Create new creator
        const { error: insertError } = await supabase
          .from('creators')
          .insert({
            wallet: walletAddress,
            handle: `user_${walletAddress.substring(0, 8)}`,
            is_verified: true,
            verification_type: 'paid',
            verification_date: new Date().toISOString()
          });

        if (insertError) {
          console.error('Error creating creator:', insertError);
          throw new Error('Failed to create verified creator');
        }
      }

      console.log('✅ Verification complete!');
      
      // Update local state
      setCreatorStats({
        ...creatorStats,
        isVerified: true,
        verificationType: 'paid'
      });
      setPaymentSuccess(true);
      setIsPaying(false);

    } catch (error) {
      console.error('❌ Paid verification failed:', error);
      setPaymentError(error.message || 'Failed to process payment');
      setIsPaying(false);
    }
  };

  return (
    <WalletRequired 
      message="You need to connect your wallet to view your verification status and track your progress."
      action="view verification status"
    >
      <div className="min-h-screen bg-black text-white">
      <div className="w-full px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Shield size={32} className="text-gray-300" />
              <h1 className="text-3xl font-bold">Get Verified</h1>
            </div>
            <p className="text-gray-400">
              {creatorStats.salesCount >= 500 
                ? 'Track your sales milestone progress to earn verification'
                : 'Two ways to get verified: Reach 500 sales or get instant verification'
              }
            </p>
          </div>
          
          {/* Create Piece Button */}
          <button
            onClick={() => navigate('/create-nft')}
            className="flex items-center gap-2 px-6 py-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white rounded-lg transition-all duration-200 font-medium border border-[#2a2a2a] hover:border-[#3a3a3a] shadow-lg"
          >
            <Plus size={20} />
            <span>Create Piece</span>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Verification Milestone Component */}
            <VerificationMilestone
              salesCount={creatorStats.salesCount}
              isVerified={creatorStats.isVerified}
              verificationType={creatorStats.verificationType}
            />

            {/* Instant Verification Option - Hidden if user has 500+ sales */}
            {creatorStats.salesCount < 500 && (
              <div className="bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-[1px] border-[#3a3a3a] rounded-xl p-6 shadow-2xl">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-400/20 to-gray-600/20 border-[1px] border-gray-500/50 flex items-center justify-center shadow-lg">
                      {creatorStats.verificationType === 'paid' ? (
                        <CheckCircle className="w-6 h-6 text-green-400" />
                      ) : (
                        <Zap className="w-6 h-6 text-gray-300" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-white">Instant Premium Verification</h3>
                      <img 
                        src="/images/Paid verification.png" 
                        alt="Premium Verified" 
                        className="w-5 h-5 object-contain flex-shrink-0"
                      />
                      {creatorStats.verificationType === 'paid' && (
                        <span className="text-green-400 text-sm font-semibold">✓ Completed</span>
                      )}
                    </div>
                    <p className="text-gray-300 text-sm mb-4">
                      {creatorStats.verificationType === 'paid' 
                        ? 'You have premium verification! Keep selling - when you reach 500 sales, you\'ll automatically upgrade to the chrome badge.'
                        : "Don't want to wait? Get instantly verified with a white badge by paying just 0.1 SOL. If you later reach 500 sales, you'll automatically upgrade to the chrome badge."
                      }
                    </p>
                      
                    {creatorStats.verificationType === 'paid' ? (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-green-400">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">Premium verification active! Your white badge is displayed across the platform.</span>
                        </div>
                      </div>
                    ) : paymentSuccess ? (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-green-400">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">Verification successful! Your white badge is now active.</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        {paymentError && (
                          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                            <p className="text-red-400 text-sm">{paymentError}</p>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-gray-400">
                            <p>• Instant verification</p>
                            <p>• Premium white badge</p>
                            <p>• Stand out from the crowd</p>
                          </div>
                          
                          <button
                            onClick={handlePaidVerification}
                            disabled={isPaying || !isConnected}
                            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-lg transition-all duration-200 font-semibold border border-gray-400/50 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isPaying ? (
                              <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span>Processing...</span>
                              </>
                            ) : (
                              <>
                                <Zap className="w-5 h-5" />
                                <span>Pay 0.1 SOL</span>
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Information Sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                {/* How to Get Verified */}
                <div className="bg-black border-[1px] border-[#2a2a2a] rounded-xl p-6 shadow-2xl">
                  <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Info size={18} className="text-gray-300" />
                    How to Get Verified
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-white mb-2">Option 1: Sales Milestone</p>
                      <ol className="space-y-2 text-sm text-gray-300">
                        <li className="flex gap-3">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center text-xs font-semibold">1</span>
                          <span>List and sell your pieces</span>
                        </li>
                        <li className="flex gap-3">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center text-xs font-semibold">2</span>
                          <span>Reach 500 sales</span>
                        </li>
                        <li className="flex gap-3">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center text-xs font-semibold">3</span>
                          <span>Get verified automatically!</span>
                        </li>
                      </ol>
                    </div>
                    {creatorStats.salesCount < 500 && (
                      <div className="border-t border-[#2a2a2a] pt-4">
                        <p className="text-sm font-semibold text-white mb-2">Option 2: Instant Verification</p>
                        <ol className="space-y-2 text-sm text-gray-300">
                          <li className="flex gap-3">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center text-xs font-semibold">1</span>
                            <span>Pay 0.1 SOL</span>
                          </li>
                          <li className="flex gap-3">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center text-xs font-semibold">2</span>
                            <span>Get white badge instantly!</span>
                          </li>
                        </ol>
                      </div>
                    )}
                  </div>
                </div>

                {/* Verification Benefits */}
                <div className="bg-black border-[1px] border-[#2a2a2a] rounded-xl p-6 shadow-2xl">
                  <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <CheckCircle size={18} className="text-gray-300" />
                    Verification Benefits
                  </h2>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li>• Verified badge on your profile and pieces</li>
                    <li>• Increased trust and credibility</li>
                    <li>• Stand out from other creators</li>
                    <li>• Enhanced visibility in search results</li>
                    <li>• Build a stronger reputation</li>
                  </ul>
                </div>

                {/* Milestone Journey */}
                <div className="bg-black border-[1px] border-[#2a2a2a] rounded-xl p-6 shadow-2xl md:col-span-2">
                  <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Info size={18} className="text-gray-300" />
                    Verification Options
                  </h2>
                  
                  <div className={`grid grid-cols-1 ${creatorStats.salesCount < 500 ? 'md:grid-cols-2' : ''} gap-4`}>
                    {/* Free Verification Path */}
                    <div>
                      <p className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                        Sales Milestone Path
                        <img 
                          src="/images/Verification badge.png" 
                          alt="Verified" 
                          className="w-4 h-4 object-contain flex-shrink-0"
                        />
                      </p>
                      <ul className="space-y-2 text-sm text-gray-300">
                        <li>• <span className="font-semibold text-white">100 sales</span> - First milestone</li>
                        <li>• <span className="font-semibold text-white">250 sales</span> - Halfway there</li>
                        <li>• <span className="font-semibold text-white">500 sales</span> - Chrome badge</li>
                      </ul>
                    </div>
                    
                    {/* Paid Verification Path - Hidden at 500+ sales */}
                    {creatorStats.salesCount < 500 && (
                      <div className="border-l-0 md:border-l border-[#2a2a2a] pl-0 md:pl-4">
                        <p className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                          Premium Verification
                          <img 
                            src="/images/Paid verification.png" 
                            alt="Premium Verified" 
                            className="w-4 h-4 object-contain flex-shrink-0"
                          />
                        </p>
                        <ul className="space-y-2 text-sm text-gray-300">
                          <li>• <span className="font-semibold text-white">0.1 SOL</span> - One-time payment</li>
                          <li>• <span className="font-semibold text-white">Instant</span> - Get verified now</li>
                          <li>• <span className="font-semibold text-white">White badge</span> - Upgrades to chrome at 500 sales</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </WalletRequired>
  );
};

export default VerificationPage;

